import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { localFlags } from '../../environments/local-flags';

export type UiEnvironment =
  | 'local'
  | 'dev'
  | 'demo'
  | 'qa'
  | 'prod'
  | 'unknown';
export type ApiEnvironment =
  | 'local'
  | 'dev'
  | 'demo'
  | 'qa'
  | 'prod'
  | 'unknown';

const KNOWN_UI_ENVIRONMENTS: readonly UiEnvironment[] = [
  'local',
  'dev',
  'demo',
  'qa',
  'prod'
];
const KNOWN_API_ENVIRONMENTS: readonly ApiEnvironment[] = [
  'local',
  'dev',
  'demo',
  'qa',
  'prod'
];

function toUiEnvironment(value: string): UiEnvironment {
  return KNOWN_UI_ENVIRONMENTS.includes(value as UiEnvironment)
    ? (value as UiEnvironment)
    : 'unknown';
}

function toApiEnvironment(value: string): ApiEnvironment {
  return KNOWN_API_ENVIRONMENTS.includes(value as ApiEnvironment)
    ? (value as ApiEnvironment)
    : 'unknown';
}

/**
 * Environment-specific domain configuration
 */
interface EnvironmentDomains {
  identityApiDomain: string;
  residentApiDomain: string;
  managementApiDomain: string;
  baseWebDomain: string;
  residentWebDomain: string;
  managementWebDomain: string;
}

/**
 * Real Assist API domain configuration
 */
interface RealAssistDomains {
  apiDomain: string;
  streamingDomain: string;
}

/**
 * Generated URL configuration for the application
 */
interface EnvironmentUrls {
  identityApiUrl: string;
  residentApiUrl: string;
  baseWebUrl: string;
  residentWebUrl: string;
  managementWebUrl: string;
  managementApiUrl: string;
}

/**
 * Service that provides environment-specific configuration.
 *
 * Features:
 * - UI Environment: Detects where the application is running (localhost, prod domain, etc.)
 * - API Environment: Determines which backend APIs to target (dev, qa, prod)
 * - Dynamic URL generation using domain mapping configuration
 * - Environment-specific page title setting (shows API environment)
 * - Common configuration properties shared across all environments
 * - Resident portal-specific URL patterns and core endpoint coverage
 *
 */
@Injectable({
  providedIn: 'root'
})
export class EnvironmentConfigService {
  // ===============================
  // Service State Properties
  // ===============================

  // Tracks whether service has been initialized
  private _isConfigured = false;

  // Current environment identifiers
  private _activeUiEnvironment: UiEnvironment = 'unknown'; // WHERE the UI is running (local, prod, qa, dev)
  private _activeApiEnvironment: ApiEnvironment = 'unknown'; // WHICH API environment is being targeted (local, dev, qa, prod)

  // Generated URLs for the current environment
  private _generatedUrls: EnvironmentUrls = {} as EnvironmentUrls;

  // ===============================
  // Configuration Objects
  // ===============================

  // Common configuration properties shared across all environments
  private readonly _sharedConfig = {
    // Production reCAPTCHA site key
    reCaptchaSiteKey: '6LdDnocgAAAAAOruOo4KNYI7GwwgwfmmMpJNH3Cg',
    // Google's official test key - works on any domain including localhost
    // https://developers.google.com/recaptcha/docs/faq#id-like-to-run-automated-tests-with-recaptcha.-what-should-i-do
    reCaptchaTestSiteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
    defaultThemeColor: 'light-blue',
    defaultRounding: 2,
    paypalCheckoutUrl: 'https://www.paypalobjects.com/api/checkout.js'
  };

  // Environment-specific domain mappings for URL generation
  private readonly _domainMappings: Record<string, EnvironmentDomains> = {
    dev: {
      identityApiDomain: 'identity-api.dev.ciranet.com',
      residentApiDomain: 'resident-api.dev.ciranet.com',
      managementApiDomain: 'management-api.dev.ciranet.com',
      baseWebDomain: 'dev.ciranet.com',
      residentWebDomain: 'resident.dev.ciranet.com',
      managementWebDomain: 'management.dev.ciranet.com'
    },
    qa: {
      identityApiDomain: 'identity-api.qa.ciranet.com',
      residentApiDomain: 'resident-api.qa.ciranet.com',
      managementApiDomain: 'management-api.qa.ciranet.com',
      baseWebDomain: 'qa.ciranet.com',
      residentWebDomain: 'resident.qa.ciranet.com',
      managementWebDomain: 'management.qa.ciranet.com'
    },
    demo: {
      identityApiDomain: 'identity-api.demo.ciranet.com',
      residentApiDomain: 'resident-api.demo.ciranet.com',
      managementApiDomain: 'management-api.demo.ciranet.com',
      baseWebDomain: 'demo.ciranet.com',
      residentWebDomain: 'resident.demo.ciranet.com',
      managementWebDomain: 'management.demo.ciranet.com'
    },
    prod: {
      identityApiDomain: 'identity-api.ciranet.com',
      residentApiDomain: 'resident-api.ciranet.com',
      managementApiDomain: 'management-api.ciranet.com',
      baseWebDomain: 'ciranet.com',
      residentWebDomain: 'resident.ciranet.com',
      managementWebDomain: 'management.ciranet.com'
    },
    local: {
      identityApiDomain: 'identity-api.local.ciranet.com',
      residentApiDomain: 'resident-api.local.ciranet.com',
      managementApiDomain: 'management-api.local.ciranet.com',
      baseWebDomain: 'local.ciranet.com',
      residentWebDomain: 'resident.local.ciranet.com',
      managementWebDomain: 'management.local.ciranet.com'
    },
    unknown: {
      identityApiDomain: 'identity-api.dev.ciranet.com',
      residentApiDomain: 'resident-api.dev.ciranet.com',
      managementApiDomain: 'management-api.dev.ciranet.com',
      baseWebDomain: 'dev.ciranet.com',
      residentWebDomain: 'resident.dev.ciranet.com',
      managementWebDomain: 'management.dev.ciranet.com'
    }
  };

  // Real Assist API domain mappings per environment
  private readonly _realAssistDomainMappings: Record<
    string,
    RealAssistDomains
  > = {
    dev: {
      apiDomain: 'rag-api.dev.realmanage.io',
      streamingDomain: 'rag.dev.realmanage.io'
    },
    qa: {
      apiDomain: 'rag-api.qa.realmanage.io',
      streamingDomain: 'rag.qa.realmanage.io'
    },
    demo: {
      apiDomain: 'rag-api.demo.realmanage.io',
      streamingDomain: 'rag.demo.realmanage.io'
    },
    prod: {
      apiDomain: 'rag-api.realmanage.io',
      streamingDomain: 'rag.realmanage.io'
    },
    local: {
      apiDomain: 'rag-api.dev.realmanage.io',
      streamingDomain: 'rag.dev.realmanage.io'
    },
    unknown: {
      apiDomain: 'rag-api.dev.realmanage.io',
      streamingDomain: 'rag.dev.realmanage.io'
    }
  };

  // ===============================
  // Public Methods
  // ===============================

  // Initialize service by detecting environment from current domain
  async initialize(): Promise<void> {
    // Prevent multiple initializations
    if (this._isConfigured) return;

    // Set current environments
    this.setCurrentUiEnvironment();
    this.setCurrentApiEnvironment();

    // Generate URLs based on environment
    this.buildEnvironmentUrls();

    // Set page title based on environment
    this.updatePageTitle();

    // Mark as initialized
    this._isConfigured = true;
  }

  // ===============================
  // Private Methods
  // ===============================

  /**
   * Set current UI environment based on where the application is actually running.
   * This determines the physical location/domain of the UI, not which APIs it calls.
   *
   * Examples:
   * - localhost:50050 → 'local'
   * - resident.local.ciranet.com → 'local'
   * - resident.ciranet.com → 'prod'
   * - resident.qa.ciranet.com → 'qa'
   */
  private setCurrentUiEnvironment(): void {
    const hostname = window.location.hostname;

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      this._activeUiEnvironment = 'local';
    } else if (hostname === 'resident.ciranet.com') {
      this._activeUiEnvironment = 'prod';
    } else if (hostname.endsWith('.ciranet.com')) {
      // Extract environment from subdomain (resident.qa.ciranet.com → qa)
      const parts = hostname.split('.');
      if (parts.length === 4) {
        this._activeUiEnvironment = toUiEnvironment(parts[1]);
      } else {
        this._activeUiEnvironment = 'prod';
      }
    } else {
      this._activeUiEnvironment = 'unknown';
    }
  }

  /**
   * Set current API environment based on configuration and hostname.
   * This determines which backend APIs the application will call, not where the UI runs.
   *
   * For local development, uses environment.environmentName to allow targeting any API:
   * - npm run start:local-dev → targets dev APIs
   * - npm run start:local-qa → targets qa APIs
   *
   * For deployed environments, derives from hostname:
   * - resident.ciranet.com → targets prod APIs
   * - resident.qa.ciranet.com → targets qa APIs
   */
  private setCurrentApiEnvironment(): void {
    const hostname = window.location.hostname;

    if (this.isLocalHostname(hostname)) {
      // For local hostnames, use the environment file to determine target environment
      this._activeApiEnvironment = toApiEnvironment(
        environment.environmentName || 'local'
      );
      return;
    }

    // Check if it's a .ciranet.com domain
    if (hostname.endsWith('.ciranet.com')) {
      const parts = hostname.split('.');

      if (parts.length === 3) {
        // Format: resident.ciranet.com (production)
        this._activeApiEnvironment = 'prod';
      } else if (parts.length === 4) {
        // Format: resident.env.ciranet.com
        this._activeApiEnvironment = toApiEnvironment(parts[1]);
      } else {
        // Unexpected format, fallback
        this._activeApiEnvironment = toApiEnvironment(
          environment.environmentName || 'unknown'
        );
      }
    } else {
      // Fallback to environment file
      this._activeApiEnvironment = toApiEnvironment(
        environment.environmentName || 'unknown'
      );
    }
  }

  /**
   * Build URLs based on current environment using domain mappings
   *
   * Uses the domainMappings configuration to build core resident portal URLs
   * dynamically based on the detected environment. Falls back to 'dev'
   * environment if the current environment is not found in mappings.
   */
  private buildEnvironmentUrls(): void {
    // Get domains for current API environment
    // Uses API environment to determine which backend services to call
    const domains = this._domainMappings[this._activeApiEnvironment];

    // Build core URLs using template literals with the environment domains
    this._generatedUrls = {
      identityApiUrl: `https://${domains.identityApiDomain}/api`,
      residentApiUrl: `https://${domains.residentApiDomain}/api/`,
      baseWebUrl: `https://${domains.baseWebDomain}`,
      residentWebUrl: `https://${domains.residentWebDomain}`,
      managementWebUrl: `https://${domains.managementWebDomain}`,
      managementApiUrl: `https://${domains.managementApiDomain}/api`
    };
  }

  // Set page title based on environment
  private updatePageTitle(): void {
    let title = 'Resident Portal';

    // Only append environment identifier for non-production API environments
    // Shows API environment in title so developers know which backend api they're using
    if (this._activeApiEnvironment !== 'prod') {
      title = `Resident Portal (${this._activeApiEnvironment})`;
    }

    // Update browser tab title
    document.title = title;
  }

  // ===============================
  // Public Getters
  // ===============================

  // Environment Information

  /**
   * Returns where the UI application is currently running.
   * Examples: 'local' (localhost), 'prod' (resident.ciranet.com), 'qa' (resident.qa.ciranet.com)
   * Used for: Datadog env tagging, local development detection
   */
  get currentUiEnvironment(): UiEnvironment {
    return this._activeUiEnvironment;
  }

  /**
   * Returns which API environment the application is configured to target.
   * Examples: 'dev', 'qa', 'prod'
   * Used for: API URL generation, page titles, Datadog api_env context
   */
  get currentApiEnvironment(): ApiEnvironment {
    return this._activeApiEnvironment;
  }

  get production(): boolean {
    return environment.production;
  }

  // Common Configuration Properties
  get reCaptchaSiteKey(): string {
    // Use test key for localhost to avoid domain validation errors
    if (this._activeUiEnvironment === 'local') {
      return this._sharedConfig.reCaptchaTestSiteKey;
    }
    return this._sharedConfig.reCaptchaSiteKey;
  }

  get defaultThemeColor(): string {
    return this._sharedConfig.defaultThemeColor;
  }

  get defaultRounding(): number {
    return this._sharedConfig.defaultRounding;
  }

  get paypalCheckoutUrl(): string {
    return this._sharedConfig.paypalCheckoutUrl;
  }

  // Dynamic Environment URLs
  get ccAuthenticationApiUrl(): string {
    return `${this._generatedUrls.identityApiUrl}/auth/`;
  }

  get ccUserApiUrl(): string {
    return `${this._generatedUrls.identityApiUrl}/user/`;
  }

  get ccResidentPortalApiUrl(): string {
    return this._generatedUrls.residentApiUrl || '';
  }

  get ccManagementPortalWebUrl(): string {
    return this._generatedUrls.managementWebUrl || '';
  }

  get ccManagementPortalApiUrl(): string {
    return `${this._generatedUrls.managementApiUrl}/`;
  }

  get identityApiUrl(): string {
    return `${this._generatedUrls.identityApiUrl}/`;
  }

  get portalSelectionUrl(): string {
    return this._generatedUrls.baseWebUrl || '';
  }

  get mainCiraHelpUrl(): string {
    return `${this._generatedUrls.residentWebUrl}/cirahelp{HELP_ID}{CONTEXT}?portal=rp`;
  }

  get ccChatButtonScript(): string {
    return `<script src="${this._generatedUrls.baseWebUrl}/CiraChat/Chat/Support-Image-Button.js.aspx?ChatDepartment={0}{1}"></script>`;
  }

  get residentPortalUrl(): string {
    return this._generatedUrls.residentWebUrl || '';
  }

  get oneTimePaymentResultUrl(): string {
    return `${this._generatedUrls.residentWebUrl}/accounts/one-time-payment/`;
  }

  get oneTimePaymentResultIntegratedUrl(): string {
    return `${this._generatedUrls.residentWebUrl}/assets/html/`;
  }

  // SparkPost API URL mappings per environment
  private readonly _sparkpostApiMappings: Record<ApiEnvironment, string> = {
    local: 'http://localhost:4000',
    dev: 'https://sparkpost.dev.realmanage.io',
    qa: 'https://sparkpost.qa.realmanage.io',
    demo: 'https://sparkpost.demo.realmanage.io', // feature disabled in demo (getter returns null); never point demo at dev — demo host not yet provisioned
    prod: 'https://sparkpost.realmanage.io',
    unknown: 'https://sparkpost.dev.realmanage.io'
  };

  // Meeting Hub MFE URLs
  private readonly _meetingHubMfeMappings: Record<
    ApiEnvironment,
    string | null
  > = {
    local: 'http://localhost:4201/remoteEntry.json',
    dev: 'https://meeting-hub.dev.realmanage.io/remoteEntry.json',
    qa: 'https://meeting-hub.qa.realmanage.io/remoteEntry.json',
    demo: 'https://meeting-hub.demo.realmanage.io/remoteEntry.json',
    prod: 'https://meeting-hub.realmanage.io/remoteEntry.json',
    unknown: null
  };

  get meetingHubMfe(): string {
    return this.requireMfeUrl(
      'Meeting Hub',
      this._meetingHubMfeMappings[this._activeApiEnvironment]
    );
  }

  // Meeting Hub API URLs
  private readonly _meetingHubApiMappings: Record<ApiEnvironment, string> = {
    local: 'https://meetinghub-api.dev.realmanage.io',
    dev: 'https://meetinghub-api.dev.realmanage.io',
    qa: 'https://meetinghub-api.qa.realmanage.io',
    demo: 'https://meetinghub-api.demo.realmanage.io',
    prod: 'https://meetinghub-api.realmanage.io',
    unknown: 'https://meetinghub-api.dev.realmanage.io'
  };

  get meetingHubApiUrl(): string {
    return this._meetingHubApiMappings[this._activeApiEnvironment];
  }

  // Real Assist API URLs
  get realAssistApiUrl(): string {
    const domains = this._realAssistDomainMappings[this._activeApiEnvironment];
    return `https://${domains.apiDomain}`;
  }

  get realAssistStreamingUrl(): string {
    const domains = this._realAssistDomainMappings[this._activeApiEnvironment];
    return `https://${domains.streamingDomain}/realassist`;
  }

  // Reservation System MFE — exposes both the amenity-reservations page
  // (mfe-amenity-reservations-page) and the amenity-calendar page
  // (mfe-amenity-calendar-page) from a single bundle. Demo is `null`
  // (feature off) so demo users never hit a broken/half-provisioned feature —
  // there is no demo reservation stack yet, and demo must never point at dev.
  // `prod` is intentionally `null` until a server-side
  // gate (community-id allowlist, role gate, or feature-flag service) is in
  // place — the current URL-flag-only opt-in is not sufficient to swap in
  // remote third-party code in production. Re-enable when the gate ships.
  // SUPPLY-CHAIN TODO (pre-GA): swap to content-hashed bundle filenames
  // (`amenity-reservations-mfe.<sha>.js`) and configure host CSP `script-src`
  // to allowlist the specific MFE origin so a compromised origin cannot push
  // arbitrary bundles. Native `import()` does not honour `integrity`.
  private readonly _reservationSystemMfeMappings: Record<
    ApiEnvironment,
    string | null
  > = {
    local: 'http://localhost:4200/amenity-reservations-mfe.js',
    dev: 'https://reservation-system.dev.realmanage.io/amenity-reservations-mfe.js',
    qa: 'https://reservation-system.qa.realmanage.io/amenity-reservations-mfe.js',
    demo: null,
    prod: null,
    unknown: null
  };

  // Both pages currently share one bundle, so both getters resolve from the
  // same mapping. If the bundles ever split, introduce a second mapping and
  // route the matching getter through it.
  get amenityReservationsMfe(): string {
    return this.requireMfeUrl(
      'Amenity Reservations',
      this._reservationSystemMfeMappings[this._activeApiEnvironment]
    );
  }

  get calendarMfe(): string {
    return this.requireMfeUrl(
      'Calendar',
      this._reservationSystemMfeMappings[this._activeApiEnvironment]
    );
  }

  // Survey feature toggle — keyed by API environment so each environment can be
  // enabled/disabled independently without touching survey-specific files.
  // Full runtime toggle (no redeployment) would require a feature-flag service.
  get surveyEnabled(): boolean {
    return this._activeApiEnvironment === 'prod';
  }

  get sparkpostApiUrl(): string | null {
    if (!this.emailSuppressionEnabled) {
      return null;
    }
    if (this.isLocalHostname(window.location.hostname)) {
      return localFlags.sparkpostLocalUrl;
    }
    return this._sparkpostApiMappings[this._activeApiEnvironment];
  }

  get emailSuppressionEnabled(): boolean {
    if (this.isLocalHostname(window.location.hostname)) {
      return localFlags.emailSuppressionEnabled;
    }
    return (
      this._activeApiEnvironment === 'dev' ||
      this._activeApiEnvironment === 'qa' ||
      this._activeApiEnvironment === 'prod'
    );
  }

  // True for localhost, loopback (IPv4 + IPv6), and the *.local.ciranet.com IIS dev domain.
  // Replaces the previous `hostname.includes('local')` substring match, which was broadened
  // to avoid false positives on arbitrary hostnames containing the word "local".
  // Used consistently for all local-environment checks in this service.
  private isLocalHostname(hostname: string): boolean {
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '::1' ||
      hostname.endsWith('.local.ciranet.com')
    );
  }

  // Fail-closed lookup helper used by MFE URL getters. Throws when the active
  // environment has no configured URL so the wrapper component surfaces an
  // error instead of silently fetching a different environment's bundle.
  private requireMfeUrl(name: string, url: string | null): string {
    if (!url) {
      throw new Error(
        `${name} MFE not configured for environment "${this._activeApiEnvironment}".`
      );
    }
    return url;
  }
}
